import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  alpha, Box, Button, CircularProgress,
  Container, Grid, Stack, TextField, Typography,
} from '@mui/material';
import {
  AccountTree as BureauIcon,
  ArrowForward as ArrowForwardIcon,
  Assignment as RequirementIcon,
  Business as DepartmentIcon,
  DashboardCustomize as DashboardIcon,
  Event as EventIcon,
  Groups as UsersIcon,
  Handshake as PartnerIcon,
  SupervisorAccount as ManagementIcon,
  Task as TaskIcon,
  TrendingUp as TrendingUpIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, ArcElement, BarElement,
  CategoryScale, LinearScale, Tooltip, Legend,
} from 'chart.js';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../common/Header';
import Loading from '../common/Loading';
import StatCards from '../common/StatCards';
import FeedbackMessage from '../common/FeedbackMessage';
import {
  ACCENT, ACCENT_2, ACCENT_3, TEXT_PRIMARY, TEXT_SECONDARY,
  glassCardSx, RADIUS_LG,
} from '../common/SaaSTokens';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const KPI_ICONS = {
  members: UsersIcon,
  managements: ManagementIcon,
  bureaus: BureauIcon,
  departments: DepartmentIcon,
  tasks: TaskIcon,
  events: EventIcon,
  partners: PartnerIcon,
  requirements: RequirementIcon,
  companies: DepartmentIcon,
};

const KPI_COLORS = ['primary', 'info', 'success', 'warning', 'secondary', 'error'];
const CHART_COLORS = [ACCENT, ACCENT_2, ACCENT_3, '#34d399', '#f87171', '#93c5fd'];

const CHART_TOOLTIP = {
  backgroundColor: alpha('#111827', 0.97),
  titleColor: TEXT_PRIMARY,
  bodyColor: TEXT_SECONDARY,
  borderColor: alpha(ACCENT, 0.25),
  borderWidth: 1,
  padding: 10,
  cornerRadius: 8,
};

const baseChartOpts = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        color: TEXT_SECONDARY,
        font: { family: "'JetBrains Mono', monospace", size: 10 },
        boxWidth: 8,
        padding: 12,
      },
    },
    tooltip: CHART_TOOLTIP,
  },
};

const barChartOpts = {
  ...baseChartOpts,
  scales: {
    x: {
      ticks: { color: alpha(TEXT_SECONDARY, 0.7), font: { family: "'JetBrains Mono', monospace", size: 10 } },
      grid: { color: alpha('#fff', 0.04) },
    },
    y: {
      ticks: { color: alpha(TEXT_SECONDARY, 0.7), precision: 0, font: { family: "'JetBrains Mono', monospace", size: 10 } },
      grid: { color: alpha('#fff', 0.04) },
    },
  },
};

const mkDoughnut = (segs) => ({
  labels: segs.map((s) => s.name || s.label),
  datasets: [{
    data: segs.map((s) => s.value),
    backgroundColor: CHART_COLORS.slice(0, segs.length),
    borderWidth: 0,
    hoverOffset: 6,
  }],
});

const mkBar = (segs) => ({
  labels: segs.map((s) => s.label),
  datasets: [{
    data: segs.map((s) => s.value),
    backgroundColor: CHART_COLORS.slice(0, segs.length),
    borderRadius: 6,
    maxBarThickness: 32,
  }],
});

const filterChart = (arr) => (arr ?? []).filter((s) => s.value > 0);

const SectionHeading = ({ children }) => (
  <Typography
    sx={{
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: '0.65rem',
      fontWeight: 600,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color: alpha(ACCENT, 0.8),
      mb: 2,
      mt: 0.5,
    }}
  >
    {children}
  </Typography>
);

const ChartCard = ({ title, children, height = 240 }) => (
  <Box sx={{ ...glassCardSx, p: 2.5, height: '100%' }}>
    <Typography
      sx={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: '0.65rem',
        fontWeight: 600,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: alpha(ACCENT, 0.85),
        mb: 2,
      }}
    >
      {title}
    </Typography>
    <Box sx={{ height }}>{children}</Box>
  </Box>
);

const ChartBody = ({ loading, data, type, emptyMsg }) => {
  if (loading) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={28} sx={{ color: ACCENT }} />
      </Box>
    );
  }
  if (!data) {
    return (
      <Typography
        sx={{
          fontFamily: "'DM Sans', sans-serif",
          color: alpha(TEXT_SECONDARY, 0.65),
          textAlign: 'center',
          pt: 6,
          fontSize: '0.85rem',
        }}
      >
        {emptyMsg}
      </Typography>
    );
  }
  return type === 'doughnut'
    ? <Doughnut data={data} options={baseChartOpts} />
    : <Bar data={data} options={barChartOpts} />;
};

const RecentList = ({ title, items = [], emptyLabel, onViewAll, loading }) => (
  <Box sx={{ ...glassCardSx, p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
      <Typography
        sx={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: '0.65rem',
          fontWeight: 600,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: alpha(ACCENT, 0.85),
        }}
      >
        {title}
      </Typography>
      {onViewAll && (
        <Button
          size="small"
          endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
          onClick={onViewAll}
          sx={{
            fontFamily: "'DM Sans', sans-serif",
            color: alpha(TEXT_SECONDARY, 0.85),
            textTransform: 'none',
            fontSize: '0.75rem',
            fontWeight: 600,
            minWidth: 0,
            p: 0,
            '&:hover': { color: ACCENT, background: 'transparent' },
          }}
        >
          View all
        </Button>
      )}
    </Stack>

    {loading ? (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={24} sx={{ color: ACCENT }} />
      </Box>
    ) : items.length === 0 ? (
      <Typography
        sx={{
          fontFamily: "'DM Sans', sans-serif",
          color: alpha(TEXT_SECONDARY, 0.65),
          fontSize: '0.85rem',
          py: 2,
          textAlign: 'center',
        }}
      >
        {emptyLabel}
      </Typography>
    ) : (
      <Stack spacing={1} sx={{ flex: 1 }}>
        {items.map((item, i) => (
          <Box
            key={item.id ?? i}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 1.5,
              py: 1.1,
              borderRadius: RADIUS_LG,
              background: alpha('#020617', 0.45),
              border: `1px solid ${alpha(ACCENT_2, 0.14)}`,
              transition: 'border-color 0.2s, background 0.2s',
              '&:hover': {
                borderColor: alpha(ACCENT, 0.35),
                background: alpha(ACCENT, 0.06),
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '0.62rem',
                fontWeight: 600,
                color: alpha(ACCENT, 0.65),
                minWidth: 24,
              }}
            >
              {String(i + 1).padStart(2, '0')}
            </Typography>
            <Typography
              sx={{
                flex: 1,
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '0.875rem',
                fontWeight: 500,
                color: TEXT_PRIMARY,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.name}
            </Typography>
            {item.meta && (
              <Typography
                sx={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '0.62rem',
                  color: alpha(TEXT_SECONDARY, 0.75),
                  textTransform: 'capitalize',
                }}
              >
                {item.meta}
              </Typography>
            )}
          </Box>
        ))}
      </Stack>
    )}
  </Box>
);

const QuickNav = ({ items }) => {
  const navigate = useNavigate();
  return (
    <Box sx={{ mt: 3 }}>
      <SectionHeading>Quick access</SectionHeading>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {items.map(({ label, path, Icon }) => (
          <Button
            key={path}
            size="small"
            startIcon={<Icon sx={{ fontSize: 16 }} />}
            onClick={() => navigate(path)}
            sx={{
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '0.8rem',
              color: TEXT_PRIMARY,
              background: alpha(ACCENT, 0.1),
              border: `1px solid ${alpha(ACCENT, 0.28)}`,
              borderRadius: '10px',
              px: 1.75,
              py: 0.75,
              '&:hover': {
                background: alpha(ACCENT, 0.18),
                borderColor: alpha(ACCENT, 0.45),
                color: '#fff',
              },
            }}
          >
            {label}
          </Button>
        ))}
      </Stack>
    </Box>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const today = new Date().toISOString().slice(0, 10);
  const [reportStart, setReportStart] = useState(`${today.slice(0, 8)}01`);
  const [reportEnd, setReportEnd] = useState(today);
  const [reportLoading, setReportLoading] = useState(false);

  const isAdmin = Boolean(user?.is_admin);

  const generateReport = async () => {
    if (!reportStart || !reportEnd || reportStart > reportEnd) {
      setError('Choose a valid report period. The start date must be on or before the end date.');
      return;
    }
    try {
      setReportLoading(true);
      setError('');
      const response = await api.get('/dashboard/report', {
        params: { start_date: reportStart, end_date: reportEnd },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dashboard-report-${reportStart}-to-${reportEnd}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError('Could not generate the dashboard report. Please try again.');
    } finally {
      setReportLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get('/dashboard/stats');
        if (!cancelled) setStats(res.data);
      } catch {
        if (!cancelled) setError('Could not load dashboard analytics. Please refresh.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const charts = useMemo(() => ({
    bureau: mkDoughnut(filterChart(stats?.charts?.bureau_distribution)),
    mgmt: mkDoughnut(filterChart(stats?.charts?.management_distribution)),
    tasks: mkBar(filterChart(stats?.charts?.tasks_by_status)),
    partners: mkBar(filterChart(stats?.charts?.partners_by_status)),
    reqs: mkBar(filterChart(stats?.charts?.requirements_by_status)),
  }), [stats]);

  const hasChart = (key) => filterChart(stats?.charts?.[key])?.length > 0;
  const showPartnership = stats?.modules?.includes('partnership');

  const kpiStats = useMemo(
    () => (stats?.kpis ?? []).map((kpi, i) => ({
      icon: KPI_ICONS[kpi.key] || TrendingUpIcon,
      value: kpi.value,
      label: kpi.label,
      color: KPI_COLORS[i % KPI_COLORS.length],
    })),
    [stats],
  );

  const adminInsightStats = useMemo(() => {
    if (stats?.view !== 'admin') return [];
    return [
      {
        icon: ManagementIcon,
        value: `${stats.analytics?.assigned_management_pct ?? 0}%`,
        label: 'Management assignment',
        color: 'primary',
        progressValue: stats.analytics?.assigned_management_pct ?? 0,
      },
      {
        icon: BureauIcon,
        value: `${stats.analytics?.assigned_bureau_pct ?? 0}%`,
        label: `${stats.analytics?.unassigned_bureau ?? 0} members without bureau`,
        color: 'info',
        progressValue: stats.analytics?.assigned_bureau_pct ?? 0,
      },
    ];
  }, [stats]);

  const opsInsightStats = useMemo(() => {
    if (stats?.view !== 'operations') return [];
    const rows = [
      {
        icon: TaskIcon,
        value: `${stats.analytics?.task_completion_pct ?? 0}%`,
        label: `${stats.analytics?.open_tasks ?? 0} open tasks`,
        color: 'success',
        progressValue: stats.analytics?.task_completion_pct ?? 0,
      },
      {
        icon: EventIcon,
        value: stats.analytics?.upcoming_events ?? 0,
        label: 'Upcoming events from today',
        color: 'info',
      },
    ];
    if (showPartnership) {
      rows.push({
        icon: PartnerIcon,
        value: stats.analytics?.active_partners ?? 0,
        label: `${stats.analytics?.active_partners_pct ?? 0}% of total partners active`,
        color: 'secondary',
      });
    }
    return rows;
  }, [stats, showPartnership]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
        <Loading message="Loading dashboard" size={56} variant="primary" />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>
      <Header
        title={isAdmin ? 'Admin Dashboard' : 'Operations Dashboard'}
        subtitle={
          isAdmin
            ? 'Organization analytics across members, bureaus, and departments'
            : 'Workload, events, and partnership activity at a glance'
        }
        icon={DashboardIcon}
        actionButton={
          isAdmin ? (
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/users')}
              sx={{
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 700,
                fontSize: '0.875rem',
                borderRadius: '10px',
                px: 3,
                py: 1.5,
                background: alpha(ACCENT, 0.16),
                border: `1px solid ${alpha(ACCENT, 0.4)}`,
                color: ACCENT,
                boxShadow: 'none',
                '&:hover': { background: alpha(ACCENT, 0.22), color: '#fff' },
              }}
            >
              Manage users
            </Button>
          ) : undefined
        }
      />

      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError('')}
        sx={{ mb: 2 }}
      />

      <Box sx={{ ...glassCardSx, p: 2.25, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-end' }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 700, color: TEXT_PRIMARY }}>Generate period report</Typography>
            <Typography sx={{ mt: 0.4, fontSize: '0.8rem', color: TEXT_SECONDARY }}>
              Export activity, follow-ups, requirements, maintenance, inventory, documents, attendance, and financial analysis.
            </Typography>
          </Box>
          <TextField
            type="date"
            label="From"
            value={reportStart}
            onChange={(event) => setReportStart(event.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            type="date"
            label="To"
            value={reportEnd}
            onChange={(event) => setReportEnd(event.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <Button
            variant="contained"
            startIcon={reportLoading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
            onClick={generateReport}
            disabled={reportLoading}
            sx={{ minHeight: 40, textTransform: 'none', fontWeight: 700 }}
          >
            {reportLoading ? 'Generating…' : 'Generate report'}
          </Button>
        </Stack>
      </Box>

      {kpiStats.length > 0 && (
        <>
          <SectionHeading>System metrics</SectionHeading>
          <StatCards stats={kpiStats} />
        </>
      )}

      {stats?.view === 'admin' && (
        <>
          <SectionHeading>Assignment intelligence</SectionHeading>
          <StatCards stats={adminInsightStats} compact />

          <SectionHeading>Distribution charts</SectionHeading>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {[
              { title: 'Members by bureau', chartKey: 'bureau_distribution', dataKey: 'bureau' },
              { title: 'Members by management', chartKey: 'management_distribution', dataKey: 'mgmt' },
            ].map(({ title, chartKey, dataKey }) => (
              <Grid key={title} size={{ xs: 12, md: 6 }}>
                <ChartCard title={title}>
                  <ChartBody
                    loading={loading}
                    data={hasChart(chartKey) ? charts[dataKey] : null}
                    type="doughnut"
                    emptyMsg="No data yet"
                  />
                </ChartCard>
              </Grid>
            ))}
          </Grid>

          <SectionHeading>Recent activity</SectionHeading>
          <Grid container spacing={2.5} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <RecentList
                title="Recently updated members"
                items={stats?.recent?.users ?? []}
                emptyLabel="No members yet"
                onViewAll={() => navigate('/users')}
                loading={loading}
              />
            </Grid>
          </Grid>

          <QuickNav
            items={[
              { label: 'Users', path: '/users', Icon: UsersIcon },
              { label: 'Managements', path: '/managements', Icon: ManagementIcon },
              { label: 'Bureaus', path: '/bureaus', Icon: BureauIcon },
              { label: 'Departments', path: '/departments', Icon: DepartmentIcon },
            ]}
          />
        </>
      )}

      {stats?.view === 'operations' && (
        <>
          <SectionHeading>Operational intelligence</SectionHeading>
          <StatCards stats={opsInsightStats} compact />

          <SectionHeading>Status charts</SectionHeading>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {[
              { title: 'Tasks by status', chartKey: 'tasks_by_status', dataKey: 'tasks', show: true },
              { title: 'Partners by status', chartKey: 'partners_by_status', dataKey: 'partners', show: showPartnership },
              { title: 'Requirements by status', chartKey: 'requirements_by_status', dataKey: 'reqs', show: showPartnership },
            ]
              .filter((c) => c.show)
              .map(({ title, chartKey, dataKey }) => (
                <Grid key={title} size={{ xs: 12, md: showPartnership ? 4 : 6 }}>
                  <ChartCard title={title}>
                    <ChartBody
                      loading={loading}
                      data={hasChart(chartKey) ? charts[dataKey] : null}
                      type="bar"
                      emptyMsg="No data in scope"
                    />
                  </ChartCard>
                </Grid>
              ))}
          </Grid>

          <SectionHeading>Recent activity</SectionHeading>
          <Grid container spacing={2.5} sx={{ mb: 2 }}>
            {[
              { title: 'Recent tasks', items: stats?.recent?.tasks, empty: 'No tasks', nav: '/tasks' },
              { title: 'Upcoming events', items: stats?.recent?.events, empty: 'No upcoming events', nav: '/events' },
              ...(showPartnership
                ? [{ title: 'Recent requirements', items: stats?.recent?.requirements, empty: 'No requirements', nav: '/requirements' }]
                : []),
            ].map(({ title, items, empty, nav }) => (
              <Grid key={title} size={{ xs: 12, md: showPartnership ? 4 : 6 }}>
                <RecentList
                  title={title}
                  items={items ?? []}
                  emptyLabel={empty}
                  onViewAll={() => navigate(nav)}
                  loading={loading}
                />
              </Grid>
            ))}
          </Grid>

          <QuickNav
            items={[
              { label: 'Tasks', path: '/tasks', Icon: TaskIcon },
              { label: 'Events', path: '/events', Icon: EventIcon },
              ...(showPartnership
                ? [
                  { label: 'Partners', path: '/partners', Icon: PartnerIcon },
                  { label: 'Requirements', path: '/requirements', Icon: RequirementIcon },
                  { label: 'Companies', path: '/companies', Icon: DepartmentIcon },
                ]
                : []),
            ]}
          />
        </>
      )}

      <Box
        sx={{
          mt: 4,
          pt: 2,
          borderTop: `1px solid ${alpha(ACCENT_2, 0.12)}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography
          sx={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '0.6rem',
            color: alpha(TEXT_SECONDARY, 0.55),
            letterSpacing: '0.08em',
          }}
        >
          Data refreshed on load
        </Typography>
        <Typography
          sx={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '0.6rem',
            color: alpha(TEXT_SECONDARY, 0.55),
            letterSpacing: '0.08em',
          }}
        >
          UTM Robocon · Est. 2002
        </Typography>
      </Box>
    </Container>
  );
};

export default Dashboard;
